import React, { useState, useEffect } from 'react'
import PaymentMethodForm from '../components/PaymentMethodForm'
import { apiService } from '../services/apiService'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CreditCardIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const PaymentMethodsPage = () => {
  const [paymentMethods, setPaymentMethods] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingMethod, setEditingMethod] = useState(null)

  useEffect(() => {
    loadPaymentMethods()
  }, [])

  const loadPaymentMethods = async () => {
    try {
      setLoading(true)
      const result = await apiService.getPaymentMethods()
      console.log('Formas de pagamento carregadas:', result.data)
      setPaymentMethods(result.data || [])
    } catch (error) {
      console.error('Erro ao carregar formas de pagamento:', error)
      setPaymentMethods([])
      toast.error('Erro ao carregar formas de pagamento')
    } finally {
      setLoading(false)
    }
  }

  const filteredMethods = paymentMethods.filter(method => {
    const searchLower = searchQuery.toLowerCase()
    return (
      method.name.toLowerCase().includes(searchLower) ||
      method.type.toLowerCase().includes(searchLower)
    )
  })

  const handleAdd = () => {
    setEditingMethod(null)
    setShowForm(true)
  }

  const handleEdit = (method) => {
    setEditingMethod(method)
    setShowForm(true)
  }

  const handleView = (method) => {
    toast(`Visualizando forma de pagamento: ${method.name}`, {
      icon: 'ℹ️',
    })
  }

  const handleDelete = async (id, name) => {
    if (!confirm(`Tem certeza que deseja desativar a forma de pagamento "${name}"?`)) {
      return
    }

    try {
      await apiService.deletePaymentMethod(id)
      toast.success('Forma de pagamento desativada com sucesso!')
      loadPaymentMethods()
    } catch (error) {
      console.error('Erro ao desativar forma de pagamento:', error)
      toast.error('Erro ao desativar forma de pagamento')
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingMethod(null)
  }

  const handleFormSuccess = () => {
    loadPaymentMethods()
    setShowForm(false)
    setEditingMethod(null)
  }

  const getTypeLabel = (type) => {
    const types = {
      'cash': 'Dinheiro',
      'pix': 'PIX',
      'debit_card': 'Cartão de Débito',
      'credit_card': 'Cartão de Crédito',
      'bank_slip': 'Boleto Bancário',
      'bank_transfer': 'Transferência Bancária'
    }
    return types[type] || type
  }

  const getTypeColor = (type) => {
    const colors = {
      'cash': 'bg-green-100 text-green-800',
      'pix': 'bg-purple-100 text-purple-800',
      'debit_card': 'bg-blue-100 text-blue-800',
      'credit_card': 'bg-red-100 text-red-800',
      'bank_slip': 'bg-yellow-100 text-yellow-800',
      'bank_transfer': 'bg-indigo-100 text-indigo-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando formas de pagamento...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <CreditCardIcon className="h-8 w-8 text-blue-600 mr-3" />
            Formas de Pagamento
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie as formas de pagamento aceitas
          </p>
        </div>

        <button
          onClick={handleAdd}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Nova Forma</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou tipo..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{paymentMethods.length}</p>
            </div>
            <BanknotesIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">À Vista</p>
              <p className="text-2xl font-bold text-gray-900">
                {paymentMethods.filter(m => !m.installments_allowed).length}
              </p>
            </div>
            <BanknotesIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Parceladas</p>
              <p className="text-2xl font-bold text-gray-900">
                {paymentMethods.filter(m => m.installments_allowed).length}
              </p>
            </div>
            <CreditCardIcon className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Com Taxa</p>
              <p className="text-2xl font-bold text-gray-900">
                {paymentMethods.filter(m => m.fee_percentage > 0).length}
              </p>
            </div>
            <CreditCardIcon className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
      </div>

      {/* Lista de Formas de Pagamento */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {filteredMethods.length === 0 ? (
          <div className="text-center py-12">
            <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'Nenhuma forma encontrada' : 'Nenhuma forma de pagamento cadastrada'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery
                ? 'Tente ajustar os filtros de busca.'
                : 'Comece cadastrando uma nova forma de pagamento.'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={handleAdd}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Cadastrar primeira forma</span>
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parcelas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recebimento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taxa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMethods.map((method) => (
                  <tr key={method.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {method.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(method.type)}`}>
                        {getTypeLabel(method.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {method.installments_allowed ?
                        `Até ${method.max_installments}x` :
                        'À vista'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {method.days_to_receive === 0 ?
                        'Imediato' :
                        `${method.days_to_receive} dias`
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {method.fee_percentage > 0 ?
                        `${method.fee_percentage}%` :
                        'Sem taxa'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(method)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Visualizar"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(method)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                          title="Editar"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(method.id, method.name)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Desativar"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal do Formulário */}
      {showForm && (
        <PaymentMethodForm
          paymentMethod={editingMethod}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  )
}

export default PaymentMethodsPage